import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { msalApp } from '../helper/AuthHelper';
import { Persona, PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { Dialog, DialogType, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import I18n from '../helper/Localization';
import { Icon } from '@fluentui/react/lib/Icon';
import { fetchApiObject } from '../helper/ApiHelper'; // Adjust the import path as necessary
import { useBranding } from '../hooks/useBranding';

/**
 * styled components
 */
const AvatarContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Text = styled.p`
    margin-top: auto;
    margin-bottom: auto;
    margin-right: 19px;
    font-size: ${props => props.fontSize};
    font-weight: bold;
    color: ${props => props.textColor};
`;

/**
 * Avatar component
 */
const Avatar = () => {
    const branding = useBranding();

    /**
     * The username
     */
    const [username, setUsername] = useState('');
    /**
     * avatar image
     */
    const [img] = useState(
        'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4NCjwhRE9DVFlQRSBzdmcgIFBVQkxJQyAnLS8vVzNDLy9EVEQgU1ZHIDEuMS8vRU4nICAnaHR0cDovL3d3dy53My5vcmcvR3JhcGhpY3MvU1ZHLzEuMS9EVEQvc3ZnMTEuZHRkJz4NCjxzdmcgd2lkdGg9IjQwMXB4IiBoZWlnaHQ9IjQwMXB4IiBlbmFibGUtYmFja2dyb3VuZD0ibmV3IDMxMi44MDkgMCA0MDEgNDAxIiB2ZXJzaW9uPSIxLjEiIHZpZXdCb3g9IjMxMi44MDkgMCA0MDEgNDAxIiB4bWw6c3BhY2U9InByZXNlcnZlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPg0KPGcgdHJhbnNmb3JtPSJtYXRyaXgoMS4yMjMgMCAwIDEuMjIzIC00NjcuNSAtODQzLjQ0KSI+DQoJPHJlY3QgeD0iNjAxLjQ1IiB5PSI2NTMuMDciIHdpZHRoPSI0MDEiIGhlaWdodD0iNDAxIiBmaWxsPSIjRTRFNkU3Ii8+DQoJPHBhdGggZD0ibTgwMi4zOCA5MDguMDhjLTg0LjUxNSAwLTE1My41MiA0OC4xODUtMTU3LjM4IDEwOC42MmgzMTQuNzljLTMuODctNjAuNDQtNzIuOS0xMDguNjItMTU3LjQxLTEwOC42MnoiIGZpbGw9IiNBRUI0QjciLz4NCgk8cGF0aCBkPSJtODgxLjM3IDgxOC44NmMwIDQ2Ljc0Ni0zNS4xMDYgODQuNjQxLTc4LjQxIDg0LjY0MXMtNzguNDEtMzcuODk1LTc4LjQxLTg0LjY0MSAzNS4xMDYtODQuNjQxIDc4LjQxLTg0LjY0MWM0My4zMSAwIDc4LjQxIDM3LjkgNzguNDEgODQuNjR6IiBmaWxsPSIjQUVCNEI3Ii8+DQo8L2c+DQo8L3N2Zz4NCg=='
    );
    /**
     * visibility state of the browser message
     */
    const [browserMessageHidden, setBrowserMessageHidden] = useState(true);

    /**
     * mimics componentDidMount
     */
    useEffect(() => {
        const fetchAvatar = async () => {
            await shouldBrowserMessageBeDisplayed();
            const _msalApp = await msalApp();
            const name = _msalApp.getAccount().name;
            const userMail = _msalApp.getAccount().idToken.emails[0];
            localStorage.setItem('lastLoggedInUserMail', userMail);
            setUsername(name);
        };
        fetchAvatar();
    }, []);

    /**
     * Toggles the browser dialog
     * @param sender dummy argument to make this function work with the browser dialog onDismiss event
     * @param visible boolean value for the visible state
     */
    const toggleBrowserMessage = (sender, visible = null) => {
        if (visible == null) {
            setBrowserMessageHidden(!browserMessageHidden);
        } else {
            localStorage.setItem('BrowserMessageShown', 'true');
            setBrowserMessageHidden(visible);
        }
    };

    /**
     * Shows the browsermessage on the initial visit of the page. subsequent visits do not display the message.
     */
    const shouldBrowserMessageBeDisplayed = async () => {
        const isBrowserMessageShown = localStorage.getItem('BrowserMessageShown') === 'true';
        if (isBrowserMessageShown) {
            setBrowserMessageHidden(true);
            return;
        }

        try {
            const countingResult = await fetchApiObject('v1/OrderPosition/Count');
            const hasToBeTestedOrders =
                countingResult.countOpen +
                countingResult.countExpired +
                countingResult.countFailed > 0;

            setBrowserMessageHidden(!hasToBeTestedOrders);
        } catch (error) {
            console.error('Error fetching order position count:', error);
            setBrowserMessageHidden(true);
        }
    };

    const dialogStyles = {
        root: {
            zIndex: 1000002,
        },
    };

    const verticalFlexStyles = { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' };

    const iconStyles = {
        fontSize: '40px',
    };

    const centerTextStyles = { textAlign: 'center' };

    const dialogButtonStyles = {
        root: {
            selectors: {
                ':focus': {
                    outline: 'unset',
                },
            },
        },
    };

    return (
        <AvatarContainer>
            {username && (
                <Text textColor={branding.ui.topMenu.avatar.textColor} fontSize={branding.ui.topMenu.avatar.fontSize}>
                    {`${I18n.get().t('User_Greeting')}, ${username}`}
                </Text>
            )}
            <Persona id="avatar" imageUrl={img} size={PersonaSize.size24} hidePersonaDetails={false} imageAlt="User" />
            <Dialog
                styles={dialogStyles}
                minWidth={300}
                hidden={browserMessageHidden}
                onDismiss={(e) => {
                    toggleBrowserMessage(e, true);
                }}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    showCloseButton: false,
                }}
            >
                <div style={verticalFlexStyles}>
                    <Icon iconName="headphones" style={iconStyles} />
                    <p></p>
                    <span style={centerTextStyles}>{I18n.get().t('BrowserMessage_Text')}</span>
                    <br />
                    <span style={centerTextStyles}>{I18n.get().t('BrowserMessage_Text_Tip')}</span>
                </div>

                <DialogFooter>
                    <div style={verticalFlexStyles}>
                        <PrimaryButton
                            styles={dialogButtonStyles}
                            onClick={(e) => {
                                toggleBrowserMessage(e, true);
                            }}
                            text={I18n.get().t('BrowserMessage_Accept')}
                        />
                    </div>
                </DialogFooter>
            </Dialog>
        </AvatarContainer>
    );
};

export default Avatar;
